import React, { useEffect, useState } from "react";
import Select from "react-select";
import { useCategoryApi } from "../../api/CategoryApi";
import { handleApiError } from "../../utils/handleApiError";
import type { CategoryInput } from "../../types/Recipe";

interface CategorySelectorProps {
  categories: CategoryInput[];
  setCategories: React.Dispatch<React.SetStateAction<CategoryInput[]>>;
}

const CategorySelector: React.FC<CategorySelectorProps> = ({
  categories,
  setCategories,
}) => {
  const [availableCategories, setAvailableCategories] = useState<
    { id: number; name: string }[]
  >([]);
  const { getCategories, createCategory } = useCategoryApi();

  useEffect(() => {
    (async () => {
      try {
        const response = await getCategories();
        setAvailableCategories(response.data.data);
      } catch (err: any) {
        handleApiError(err, "Feil ved lasting av kategorier.");
      }
    })();
  }, []);

  const addCategory = () => {
    setCategories([...categories, { id: 0 }]);
  };

  const updateCategory = (index: number, categoryId: any) => {
    const updated = [...categories];
    updated[index].id = categoryId;
    setCategories(updated);
  };

  const removeCategory = (index: number) =>
    setCategories(categories.filter((_, i) => i !== index));

  const handleNewCategory = async (name: string) => {
    if (!name.trim()) return;

    try {
      const response = await createCategory(name);
      const created = response.data.data;

      // Add newly created category to list
      setAvailableCategories((prev) => [...prev, created]);

      return created;
    } catch (err: any) {
      // Backend says: "You tried to create a category that already exists"
      if (err.status === 409) {
        const existing = availableCategories.find(
          (c) => c.name.toLowerCase() === name.toLowerCase(),
        );

        if (existing) return existing;
      }

      handleApiError(err, "Feil ved oppretting av kategori.");
    }
  };

  const getOptionsForRow = (currentIndex: number) =>
    availableCategories.filter(
      (cat) =>
        !categories.some((sel, i) => i !== currentIndex && sel.id === cat.id),
    );

  return (
    <div className="border-t pt-3 mt-3">
      <h2 className="font-semibold text-lg text-orange-600 mb-2">Kategorier</h2>

      {categories.map((cat, index) => (
        <div key={index} className="flex gap-2 mb-2 items-center">
          <Select
            value={
              cat.id && cat.id !== "_new"
                ? {
                    value: cat.id,
                    label:
                      availableCategories.find((opt) => opt.id === cat.id)
                        ?.name || "",
                  }
                : cat.id === "_new"
                  ? { value: "_new", label: "➕ Lag ny/søk..." }
                  : null
            }
            onChange={(selected) => {
              if (selected?.value === "_new") {
                updateCategory(index, "_new");
              } else {
                updateCategory(index, Number(selected?.value));
              }
            }}
            options={[
              { value: "_new", label: "➕ Lag ny..." },
              ...getOptionsForRow(index).map((opt) => ({
                value: opt.id,
                label: opt.name,
              })),
            ]}
            placeholder="Søk etter kategori..."
            isClearable={false}
            className="flex-1"
          />

          {cat.id === "_new" && (
            <input
              type="text"
              placeholder="Ny kategori"
              autoFocus
              onBlur={async (e) => {
                const newOne = await handleNewCategory(e.target.value);
                if (newOne) updateCategory(index, newOne.id);
              }}
              className="flex-1 border border-gray-300 rounded-lg px-2 py-1"
            />
          )}

          <button
            type="button"
            onClick={() => removeCategory(index)}
            className="text-red-500 font-bold"
          >
            ✕
          </button>
        </div>
      ))}

      <button
        type="button"
        onClick={addCategory}
        className="bg-orange-100 text-orange-700 px-3 py-1 rounded-lg hover:bg-orange-200"
      >
        ➕ Legg til kategori
      </button>
    </div>
  );
};

export default CategorySelector;
